<template>
  <div 
    class="ai-assistant" 
    :class="{ 'is-open': isChatOpen, 'is-dragging': isDragging }"
    :style="assistantStyle"
    ref="assistantRef"
  >
    <!-- AI 浮动按钮 -->
    <div 
      class="ai-float-btn" 
      :class="{ 'is-active': isChatOpen }"
      @mousedown="startDrag"
      @touchstart.passive="startDrag"
      @click="handleClick"
    >
      <transition name="btn-icon" mode="out-in">
        <div v-if="!isChatOpen" key="ai" class="ai-icon-wrapper">
          <img src="/static/common/Ai.png" class="ai-icon" alt="AI" draggable="false" />
        </div>
        <el-icon v-else key="close" class="close-icon"><Close /></el-icon>
      </transition>
    </div>

    <!-- 聊天窗口 -->
    <transition name="chat-window">
      <div v-show="isChatOpen" class="ai-chat-window" ref="chatWindowRef">
        <!-- 标题栏 -->
        <div class="chat-header">
          <div class="header-left">
            <div class="ai-avatar-small">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
              </svg>
            </div>
            <span class="header-title">智能助手</span>
          </div>
          <div class="header-actions">
            <el-tooltip content="历史对话" placement="top" popper-class="ai-assistant-tooltip">
              <button class="action-btn" @click="showHistory = !showHistory" :class="{ 'active': showHistory }">
                <el-icon><Clock /></el-icon>
              </button>
            </el-tooltip>
            <el-tooltip content="新建对话" placement="top" popper-class="ai-assistant-tooltip">
              <button class="action-btn" @click="handleNewChat">
                <el-icon><Plus /></el-icon>
              </button>
            </el-tooltip>
            <el-tooltip content="进入对话" placement="top" popper-class="ai-assistant-tooltip">
              <button class="action-btn" @click="goToChatPage">
                <el-icon><Expand /></el-icon>
              </button>
            </el-tooltip>
            <el-tooltip content="设置" placement="top" popper-class="ai-assistant-tooltip">
              <button class="action-btn" @click="settingsVisible = !settingsVisible" :class="{ 'active': settingsVisible }">
                <el-icon><Setting /></el-icon>
              </button>
            </el-tooltip>
            <el-tooltip content="最小化" placement="top" popper-class="ai-assistant-tooltip">
              <button class="action-btn" @click="toggleChat">
                <el-icon><Minus /></el-icon>
              </button>
            </el-tooltip>
          </div>
        </div>
        <!-- 设置面板：语音唤醒开关 + 唤醒词（前端本地可视化设置，不写死服务器配置） -->
        <transition name="fade">
          <div v-show="settingsVisible" class="ai-settings-panel">
            <div class="settings-title">语音设置</div>
            <div class="settings-body">
              <div class="settings-row">
                <div class="settings-info">
                  <span class="settings-label">语音唤醒</span>
                  <span class="settings-desc">开启后后台常驻监听唤醒词，唤醒后自动开始录音</span>
                </div>
                <el-switch v-model="kwsEnabled" size="small" @change="saveKwsSetting" />
              </div>
              <div class="settings-row">
                <div class="settings-info">
                  <span class="settings-label">唤醒词</span>
                  <span class="settings-desc">语音唤醒关键词（只支持中文，如：你好虎鱼）</span>
                </div>
                <el-input
                  v-model="kwsKeywords"
                  placeholder="唤醒词"
                  size="small"
                  class="settings-input"
                  maxlength="20"
                />
              </div>
              <div class="settings-row">
                <div class="settings-info">
                  <span class="settings-label">朗读引擎</span>
                  <span class="settings-desc">本地离线速度快，在线音色更自然</span>
                </div>
                <el-radio-group v-model="ttsEngine" size="small" class="settings-radio">
                  <el-radio-button value="local">本地</el-radio-button>
                  <el-radio-button value="edgetts">在线</el-radio-button>
                </el-radio-group>
              </div>
              <div v-if="ttsEngine === 'edgetts'" class="settings-row column">
                <div class="settings-info">
                  <span class="settings-label">朗读音色</span>
                  <span class="settings-desc">在线引擎可选音色，支持中英混合</span>
                </div>
                <el-select v-model="ttsVoice" placeholder="请选择音色" size="small" style="width: 100%">
                  <el-option v-for="v in edgeTtsVoices" :key="v.id" :label="v.name" :value="v.id" />
                </el-select>
              </div>
            </div>
            <div class="settings-actions">
              <button class="settings-cancel-btn" @click="settingsVisible = false">取消</button>
              <button class="settings-confirm-btn" @click="confirmAiKwsSetting">确定</button>
            </div>
          </div>
        </transition>

        <!-- 历史对话面板 -->
        <transition name="slide-right">
          <div v-show="showHistory" class="history-panel">
            <div class="history-header">
              <div class="history-header-left">
                <button class="history-back-btn" @click="showHistory = false">
                  <el-icon><ArrowLeft /></el-icon>
                </button>
                <span class="history-title">历史对话</span>
              </div>
              <div class="history-header-right">
                <el-button type="primary" size="small" @click="newChatFromHistory">
                  <el-icon><Plus /></el-icon>新建对话
                </el-button>
                <el-button type="danger" text size="small" @click="clearAllHistory" v-if="chatHistory.length > 0">
                  清空全部
                </el-button>
              </div>
            </div>
            <div class="history-list" v-if="chatHistory.length > 0">
              <div v-for="group in groupedHistoryList" :key="group.label" class="history-group">
                <!-- 分组标题 -->
                <div class="group-header" @click="toggleGroup(group.label)">
                  <el-icon class="group-arrow" :class="{ 'is-expanded': group.expanded }">
                    <ArrowRight />
                  </el-icon>
                  <span class="group-label">{{ group.label }}</span>
                  <span class="group-count">({{ group.items.length }})</span>
                </div>
                <!-- 分组内容 -->
                <div v-show="group.expanded" class="group-items">
                  <div
                    v-for="chat in group.items"
                    :key="chat.chatId"
                    class="history-item"
                    :class="{ 'active': currentChatId === chat.chatId }"
                    @click="selectHistoryItem(chat)"
                  >
                    <div class="history-item-content">
                      <!-- 重命名输入框 -->
                      <template v-if="renameVisible && renameTarget === chat">
                        <div class="rename-input-wrapper" @click.stop>
                          <el-input
                            v-model="renameValue"
                            size="small"
                            @keyup.enter="confirmRename()"
                            @keyup.esc="renameVisible = false"
                            @blur="confirmRename()"
                            autofocus
                          />
                        </div>
                      </template>
                      <template v-else>
                        <div class="history-item-title">
                          <el-icon v-if="chat.orderNum > 0" class="pin-icon"><Top /></el-icon>
                          {{ chat.questionOverview }}
                        </div>
                      </template>
                      <div class="history-item-time">{{ formatChatTime(chat.createTime) }}</div>
                    </div>
                    <!-- 三个点更多按钮 -->
                    <div class="more-btn-wrapper" @click.stop>
                      <div class="more-btn" @click="toggleDropdown(chat.chatId)">
                        <el-icon><MoreFilled /></el-icon>
                      </div>
                      <!-- 下拉菜单 -->
                      <div v-if="activeDropdown === chat.chatId" class="dropdown-menu">
                        <div class="dropdown-item" @click="togglePin(chat); closeDropdown()">
                          <el-icon><Top /></el-icon>
                          <span>{{ chat.orderNum > 0 ? '取消置顶' : '置顶' }}</span>
                        </div>
                        <div class="dropdown-item" @click="startRename(chat); closeDropdown()">
                          <el-icon><Edit /></el-icon>
                          <span>重命名</span>
                        </div>
                        <div class="dropdown-item dropdown-item--danger" @click="handleDelete(chat); closeDropdown()">
                          <el-icon><Delete /></el-icon>
                          <span>删除</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="history-empty">
              <el-icon class="history-empty-icon"><Clock /></el-icon>
              <span>暂无历史对话</span>
            </div>
          </div>
        </transition>

        <!-- 消息区域（复用聊天页组件：消息列表 + 欢迎 + 工具链/溯源富展示） -->
        <ChatMessageList
          class="assistant-message-list"
          :messages="messages"
          :loading="isLoading"
          :thinking-enabled="thinkingEnabled"
          :messages-ref="setScrollContainer"
          @citation-click="handleCitationClick"
          @toggle-reasoning="toggleReasoningExpand"
          @toggle-tool-node="toggleToolChainNode"
          @toggle-tool-group="toggleToolChainGroup"
          @toggle-web-search="toggleWebSearchExpand"
          @toggle-turn-card="toggleTurnCard"
          @clarify="handleClarificationSend"
          @view-source="openSourcePanel"
          @view-trace="handleViewTrace"
          @speak="(msg) => speakMessage(msg._id, msg.content)"
          @regenerate="regenerateAnswer"
          @resume="handleResume"
        >
          <template #welcome>
            <ChatWelcome
              :quick-agent="effectiveAgentObj"
              :faq-tree-loading="faqTreeLoading"
              :faq-tree-data="faqTreeData"
              @pick="sendQuickQuestion"
            />
          </template>
        </ChatMessageList>

        <!-- 回到最新：用户上滑阅读时不再抢滚动位置，改为给出入口 -->
        <button v-if="scrollDetached" class="btn-scroll-latest" @click="scrollToLatest">↓ 回到最新</button>

        <!-- 底部区：快捷提问条悬浮在输入框上方 + 输入区（复用聊天页 ChatInputBar；语音模式保留按住说话） -->
        <div class="chat-input-wrapper assistant-bottom">
          <ChatQuickBar
            v-if="messages.length === 0 && !isLoading && quickQuestions.length > 0"
            :quick-questions="quickQuestions"
            @pick="sendQuickQuestion"
          />
          <!-- 澄清 / 二次确认浮窗（父子树 + 搜索；与聊天页共用同一份 ClarifyCard） -->
          <ClarifyCard
            :clarify="pendingClarify"
            :loading="isLoading"
            @choose="chooseClarify"
            @submit="submitClarifyInput"
            @dismiss="dismissClarify"
          />
          <!-- 文本输入模式：复用聊天页输入条 -->
          <ChatInputBar
            v-if="!voiceMode"
            class="assistant-input"
            :agent-list="agentList"
            :input-text="inputMessage"
            :input-placeholder="inputPlaceholder"
            :can-send="canSend"
            :loading="isLoading"
            :image-preview="imagePreview"
            :effective-agent-obj="effectiveAgentObj"
            :effective-knowledges="effectiveKnowledges"
            :context-all="contextAll"
            :context-kbs="contextKbs"
            :mentioned-agent="mentionedAgent"
            :thinking-enabled="thinkingEnabled"
            :agent-supports-image="agentSupportsImage"
            :agent-kb-count="agentKbCount"
            @update:input-text="inputMessage = $event"
            @send="handleSend"
            @cancel="handleCancel"
            @image-change="handleImageSelect"
            @remove-image="removeImage"
            @toggle-thinking="handleToggleThinking"
            @mention-agent="setMentionedAgent"
            @clear-mention="clearMentionedAgent"
            @select-all-kbs="selectAllContextKbs"
            @toggle-kb="toggleContextKb"
            @voice-toggle="toggleVoiceMode"
          />
          <!-- 语音输入模式（微信式：按住说话） -->
          <div v-else class="chat-input voice-input">
            <div
              class="hold-to-talk-btn"
              :class="{ 'is-recording': isRecording }"
              @mousedown.prevent="onHoldTalkStart"
              @mouseup="stopRecording"
              @mouseleave="stopRecording"
              @touchstart.prevent="onHoldTalkStart"
              @touchend.prevent="stopRecording"
            >
              <el-icon class="hold-icon"><Microphone /></el-icon>
              <span class="hold-text">{{ isRecording ? '松开 结束' : '按住 说话' }}</span>
            </div>
            <div v-if="partialAiText" class="hold-partial-text">{{ partialAiText }}</div>
            <el-button
              class="voice-close-btn"
              circle
              size="small"
              @click="exitVoiceMode"
              title="返回文字输入"
            >
              <el-icon><Close /></el-icon>
            </el-button>
          </div>
          <!-- 识别结果确认浮层（微信式：识别完预览文字，选择发送/取消） -->
          <transition name="voice-result">
            <div v-if="voiceResultVisible" class="voice-result-panel" @click.stop>
              <div class="voice-result-label">识别结果（可编辑）</div>
              <textarea
                v-model="voiceResultText"
                class="voice-result-edit"
                rows="2"
                resize="none"
                placeholder="可修改识别内容后发送"
              ></textarea>
              <div class="voice-result-actions">
                <button class="voice-result-cancel" @click="cancelVoiceResult">取消</button>
                <button class="voice-result-send" @click="confirmVoiceSend">发送</button>
              </div>
            </div>
          </transition>
          <div class="input-footer">
            <span v-if="kwsListening" class="ai-kws-indicator">
              <span class="ai-kws-dot"></span>唤醒监听中
            </span>
            <span class="footer-text">AI 生成内容仅供参考，请核实准确性</span>
          </div>
        </div>
      </div>
    </transition>

    <!-- 溯源覆盖层（Teleport 到 body，全屏层级）：源数据面板 / 多溯源选择器 / 溯源详情 -->
    <SourcePanel v-model:visible="sourcePanelVisible" :sources="currentSources" />
    <TraceSelectorDialog
      :visible="traceSelectorVisible"
      :msg="traceSelectorMsg"
      @update:visible="traceSelectorVisible = $event"
      @select="selectTrace"
    />
    <TracePanel
      :visible="tracePanelVisible"
      :msg="currentTraceMsg"
      :active-tab="activeTraceTab"
      :active-index="activeTraceIndex"
      :qa-traces="qaTraces"
      :doc-traces="docTraces"
      :graph-traces="graphTraces"
      @update:visible="tracePanelVisible = $event"
      @update:active-tab="activeTraceTab = $event"
      @navigate-knowledge="handleNavigateToKnowledge"
      @view-source="handleViewSourceData"
      @view-document="handleViewCiteDocument"
    />
  </div>
</template>

<script setup>
import { ref, reactive, nextTick, onMounted, onBeforeUnmount, computed } from 'vue'
import {
  Close, Minus, Setting,
  Clock, Plus, Delete, ArrowLeft, ArrowRight, Edit, Top, MoreFilled,
  Microphone, Expand
} from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import { deleteChatApi } from '@/modules/huyu-framework/backend/api/huyuAI/chat'
import { stopKwsListening } from '@/modules/huyu-framework/backend/api/huyuAI/speech'
import { useVoiceChat } from '@/modules/huyu-framework/backend/composables/useVoiceChat'
import { useChatAgent } from '@/modules/huyu-framework/backend/composables/useChatAgent'
import { useChatStream } from '@/modules/huyu-framework/backend/composables/useChatStream'
import { useChatHistory } from '@/modules/huyu-framework/backend/composables/useChatHistory'
import { useChatFaq } from '@/modules/huyu-framework/backend/composables/useChatFaq'
import { parseWebSearchResults, useChatRender } from '@/modules/huyu-framework/backend/composables/useChatRender'
import { ElMessage, ElMessageBox } from 'element-plus'
import useUserStore from '@/store/modules/user'
import ChatWelcome from '@/modules/huyu-ai/backend/views/AI/chat/components/ChatWelcome.vue'
import ChatQuickBar from '@/modules/huyu-ai/backend/views/AI/chat/components/ChatQuickBar.vue'
import ChatMessageList from '@/modules/huyu-ai/backend/views/AI/chat/components/ChatMessageList.vue'
import ChatInputBar from '@/modules/huyu-ai/backend/views/AI/chat/components/ChatInputBar.vue'
import TraceSelectorDialog from '@/modules/huyu-ai/backend/views/AI/chat/components/TraceSelectorDialog.vue'
import TracePanel from '@/modules/huyu-ai/backend/views/AI/chat/components/TracePanel.vue'
import SourcePanel from '@/modules/huyu-ai/backend/views/AI/chat/components/SourcePanel.vue'
import ClarifyCard from '@/modules/huyu-ai/backend/views/AI/chat/components/ClarifyCard.vue'

// ==================== 权限检查（AI 助手可见性） ====================
const hasAiPermission = () => {
  const userStore = useUserStore()
  const permissions = userStore.permissions || []
  return permissions.includes('*:*:*') || permissions.includes('ai:assistant:show')
}

// ==================== 核心逻辑（5 个可复用 composable，与聊天页完全同一套） ====================
// 1) 智能体/知识库/RAG 选项 + 深度思考/智能搜索（数据层）
const agentState = useChatAgent({})
// 2) 消息流式发送 + 输入（会话基础状态：messages/loading/inputText/activeChatId）
const stream = useChatStream({
  effectiveAgent: agentState.effectiveAgent,
  effectiveAgentObj: agentState.effectiveAgentObj,
  effectiveKnowledges: agentState.effectiveKnowledges,
  contextKbs: agentState.contextKbs,
  thinkingEnabled: agentState.thinkingEnabled
})
// 3) 快捷问题卡片（动态加载 + 发送）
const faq = useChatFaq({
  selectedAgentObj: agentState.selectedAgentObj,
  handleSend: stream.handleSend,
  inputText: stream.inputText
})
// 4) 历史会话数据层 + 分组/置顶/重命名/删除
const historyState = useChatHistory({
  agentList: agentState.agentList,
  applyAgentDefaults: agentState.applyAgentDefaults,
  mentionedAgent: agentState.mentionedAgent,
  contextKbs: agentState.contextKbs,
  restoreContextKbs: agentState.restoreContextKbs,
  selectedAgent: agentState.selectedAgent,
  selectedRagType: agentState.selectedRagType,
  messages: stream.messages,
  loading: stream.loading,
  inputText: stream.inputText,
  nextMsgId: stream.nextMsgId,
  scrollToBottom: stream.scrollToBottom,
  cancelStream: stream.cancelStream,
  activeChatId: stream.activeChatId,
  parseWebSearchResults,
  onQuickNewChat: () => {}
})
// 5) 消息渲染辅助（纯函数 + 工具链/溯源面板状态，与聊天页同一套）
const router = useRouter()
const renderState = useChatRender({ messages: stream.messages, router })

// 回填联动回调（避免 composable 间循环依赖）
agentState.setOnAgentChanged(() => { faq.loadQuickQuestions(); faq.loadFaqTree() })
stream.setOnSaveSession(historyState.saveSession)

// ==================== 解构供模板/本组件使用 ====================
const {
  agentList, thinkingEnabled, handleThinkingChange, agentSupportsImage,
  effectiveAgentObj, effectiveKnowledges, contextAll, contextKbs,
  mentionedAgent, agentKbCount, selectAllContextKbs, toggleContextKb,
  setMentionedAgent, clearMentionedAgent, loadAllOptions
} = agentState
const {
  messages, loading, inputText, activeChatId,
  inputPlaceholder, canSend,
  handleSend, handleResume, handleCancel, handleClarificationSend,
  imagePreview, handleImageSelect, removeImage,
  scrollToBottom, scrollDetached, scrollToLatest, dispose
} = stream
const { quickQuestions, loadQuickQuestions, sendQuickQuestion, faqTreeData, faqTreeLoading, loadFaqTree } = faq
const {
  historyList, groupedHistoryList, loadHistory, loadHistoryItem, handleNewChat,
  handleDelete, togglePin, toggleGroup, toggleDropdown, closeDropdown,
  startRename, confirmRename, renameVisible, renameValue, renameTarget,
  activeDropdown,
  signalMemoryFlush
} = historyState
// ==================== 消息渲染（useChatRender：工具链展开 / 溯源面板状态，与聊天页同一套） ====================
const {
  toggleReasoningExpand, toggleToolChainNode, toggleToolChainGroup, toggleWebSearchExpand,
  toggleTurnCard,
  tracePanelVisible, currentTraceMsg, activeTraceTab, activeTraceIndex,
  traceSelectorVisible, traceSelectorMsg, qaTraces, docTraces, graphTraces,
  handleViewTrace, selectTrace,
  handleNavigateToKnowledge, handleViewSourceData, handleCitationClick, handleViewCiteDocument
} = renderState

// 深度思考开关（ChatInputBar emit 后由页面切换并持久化，同聊天页）
function handleToggleThinking() {
  thinkingEnabled.value = !thinkingEnabled.value
  handleThinkingChange()
}

// ==================== 溯源源数据面板状态（与聊天页同一套逻辑） ====================
const sourcePanelVisible = ref(false)
const currentSources = ref([])
function openSourcePanel(sources) {
  currentSources.value = sources || []
  sourcePanelVisible.value = true
}

// ==================== 悬浮助手页面状态 ====================
const isChatOpen = ref(false)
const chatWindowRef = ref(null)
const assistantRef = ref(null)
// 历史面板（侧滑）
const showHistory = ref(false)
// 输入框 / 消息容器别名：指向 stream 状态，模板保持原变量名（最小改动）
const inputMessage = inputText
const isLoading = loading
// 发送 / 当前会话别名（统一走 useChatStream）
const sendMessage = handleSend
const currentChatId = activeChatId

// 右上角"进入对话"：跳转到对话页，自动选中当前智能体、携带当前输入的问题，并带当前会话 ID 以加载对应历史记录
function goToChatPage() {
  const agentCode = effectiveAgentObj.value?.agentCode || ''
  const question = (inputMessage.value || '').trim()
  const chatId = currentChatId.value || ''
  router.push({
    path: '/huyuAI/AI/chat',
    query: {
      agentCode: agentCode || undefined,
      question: question || undefined,
      chatId: chatId || undefined
    }
  })
}
// 历史列表别名（useChatHistory 的数据层）
const chatHistory = historyList

// ==================== 工具澄清（V2 确认卡浮窗）====================
// 工具返回 {clarify:true, message, hint, suggestions:[{label,value}]}（如 place_query 同名塘口消歧），
// 经 tool_call SSE 事件透传到 assistantMsg.toolCalls[].result，在此提取为浮窗状态；
// 浮窗出现即盖住输入框（强制单线程：先解决卡点再对话），点选项即发送，✕ 放弃恢复输入框。
const dismissedClarifyKeys = ref(new Set())
const pendingClarify = computed(() => {
  const arr = messages.value || []
  for (let i = arr.length - 1; i >= 0; i--) {
    const m = arr[i]
    if (m.role !== 'assistant' || !m.toolCalls || !m.toolCalls.length) continue
    // 只看最近一条含工具调用的 assistant 消息（模型已继续作答的旧澄清不复活）
    for (let j = m.toolCalls.length - 1; j >= 0; j--) {
      const tc = m.toolCalls[j]
      let r = null
      try { r = JSON.parse(tc.result || '{}') } catch (e) { r = null }
      if (r && r.clarify === true && Array.isArray(r.suggestions) && r.suggestions.length) {
        const key = (tc.toolName || '') + '|' + (tc.round ?? '') + '|' + String(tc.result || '').slice(0, 80)
        if (!dismissedClarifyKeys.value.has(key)) {
          const userPrompt = (r.user_prompt || '').trim()
          const hintText = (r.hint || '').trim()
          const fullText = (r.message || '').trim()
          // 题干优先级：user_prompt（面向用户的人话）> hint > 兜底截断；
          // message 是给模型的指令（含"请向用户确认/必须再次调用"等），不作为标题展示
          const title = userPrompt || hintText || (fullText.length > 40 ? fullText.slice(0, 40) + '…' : fullText) || '需要您的选择'
          return {
            key,
            msg: m,
            title,
            detail: (!userPrompt && !hintText && fullText && fullText !== title) ? fullText : '',
            // 工具名 + 选项结构一并保留：placeId/parentId/parentName 是点选后免重查的权威依据
            toolName: tc.toolName || '',
            options: r.suggestions.map(s => ({ ...s, label: s.label || s.value, value: s.value || s.label }))
          }
        }
      }
    }
    break
  }
  return null
})
// 点选项：回填输入框 → 走主发送管线，并把结构化字段（placeId 等）随请求下发，
// 后端据此跳过重查（此前只发文本，模型只能再猜一遍名称）。
const chooseClarify = (opt) => {
  if (isLoading.value || !opt) return
  const p = pendingClarify.value
  inputMessage.value = (opt.value || opt.label || '').toString()
  nextTick(() => sendMessage({ clarifyChoice: buildClarifyChoice(opt, p) }))
}
/** 只有带权威主键的选项才下发结构化载荷；纯文本选项不下发（保持原有语义） */
function buildClarifyChoice(opt, p) {
  if (!opt || opt.placeId == null) return undefined
  const choice = {
    toolName: (p && p.toolName) || '',
    placeId: opt.placeId,
    placeName: opt.placeName || opt.label || '',
    value: opt.value || opt.label || ''
  }
  if (opt.parentId != null) choice.parentId = opt.parentId
  if (opt.parentName) choice.parentName = opt.parentName
  return choice
}
// 自由输入：填写补充说明后提交（走主发送管线），提交后浮窗自然消失
const submitClarifyInput = (text) => {
  if (isLoading.value) return
  const val = String(text || '').trim()
  if (!val) return
  inputMessage.value = val
  nextTick(() => sendMessage())
}
/**
 * 重新生成：丢掉已生成内容，从原问题重新答一遍。
 * 与「继续生成」（handleResume）的分界：后者保留已生成内容、从断点往下写；这里从头再来。
 * 两个都要给——只留一个的话，用户想换另一种方式就得自己重打问题。
 */
const regenerateAnswer = (msg) => {
  if (isLoading.value || !msg) return
  const list = messages.value || []
  const idx = list.findIndex(m => m._id === msg._id)
  let question = ''
  for (let i = (idx >= 0 ? idx - 1 : list.length - 1); i >= 0; i--) {
    if (list[i].role === 'user') { question = list[i].content || ''; break }
  }
  if (!question.trim()) return
  inputMessage.value = question
  nextTick(() => sendMessage())
}
// ✕ 放弃：记录已放弃的 clarify（按工具名+轮次+结果摘要），浮窗消失、输入框恢复
const dismissClarify = () => {
  if (pendingClarify.value) {
    const set = new Set(dismissedClarifyKeys.value)
    set.add(pendingClarify.value.key)
    dismissedClarifyKeys.value = set
  }
}

// 历史/消息时间显示（HH:mm）
const formatChatTime = (t) => {
  if (!t) return ''
  const d = new Date(t)
  if (isNaN(d.getTime())) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
// 消息滚动容器回写：ChatMessageList 挂载后把根 DOM 交还 stream.messagesRef（供 scrollToBottom 使用）
const setScrollContainer = (el) => { stream.messagesRef.value = el }

// 知识库选择已由复用组件 ChatInputBar 的「智能体 + 知识库」上下文栏接管（原配置栏单选下拉移除）

// ==================== 语音能力（useVoiceChat 组合式复用：唤醒/流式ASR录音/TTS朗读） ====================
const {
  kwsEnabled, kwsKeywords, kwsListening, settingsVisible, saveKwsSetting,
  confirmKwsSetting: confirmAiKwsSetting,
  isRecording, partialText: partialAiText, voiceFromWake, vadRecorder,
  startRecording, stopRecording,
  speakMessage, ttsEngine, ttsVoice, edgeTtsVoices,
} = useVoiceChat({
  storageKey: 'ai_assistant_settings',
  // 悬浮助手按住说话交互：录音由 isRecording 状态接管，无需额外 UI 激活条件
  isUiActive: () => false,
  // 无 AI 助手权限时不请求接口 / 不连监听
  mountReady: () => hasAiPermission(),
  // 唤醒命中：切入语音模式并自动开始录音，8 秒兜底自动结束进入识别结果
  onWakeEnter: () => {
    voiceMode.value = true
    startHoldTalk()
    if (kwsWakeTimer) clearTimeout(kwsWakeTimer)
    kwsWakeTimer = setTimeout(async () => {
      if (isRecording.value) await stopRecording()
    }, 8000)
  },
  // 识别完成：唤醒模式直接发送；手动模式显示确认面板
  onVoiceResult: (text, fromWake) => {
    finalizeAiVoice(text, fromWake)
  },
})

// 语音模式（微信式：输入区整体切换为"按住说话"）
const voiceMode = ref(false)
let kwsWakeTimer = null // 唤醒录音 8s 兜底定时器

// 识别结果确认（微信式：识别完先预览文字，用户选择发送/取消）
const voiceResultVisible = ref(false)
const voiceResultText = ref('')

// 发送识别结果：填入输入框并直接发送
const confirmVoiceSend = () => {
  const text = voiceResultText.value
  voiceResultVisible.value = false
  voiceResultText.value = ''
  if (text) {
    inputMessage.value = text
    sendMessage()
  }
}

// 取消识别结果
const cancelVoiceResult = () => {
  voiceResultVisible.value = false
  voiceResultText.value = ''
}

// 切换语音模式
const toggleVoiceMode = () => {
  if (isRecording.value) vadRecorder.cancel()
  voiceMode.value = !voiceMode.value
}

// 退出语音模式，返回文字输入
const exitVoiceMode = () => {
  if (isRecording.value) vadRecorder.cancel()
  voiceMode.value = false
}

// 手动按住说话：重置唤醒标志后再开始录音
const onHoldTalkStart = () => {
  voiceFromWake.value = false
  startHoldTalk()
}

// 按住说话：按下开始录音（录音前暂停常驻监听，避免双麦克风）
const startHoldTalk = () => {
  if (isLoading.value || isRecording.value) return
  stopKwsListening() // 录音前暂停常驻监听，避免双麦克风
  kwsListening.value = false
  startRecording()
}

// 识别完成回调（唤醒直发 / 手动确认面板）
const finalizeAiVoice = (text, fromWake) => {
  if (text) {
    if (fromWake) {
      inputMessage.value = text
      sendMessage() // 唤醒模式：说完话直接发送
    } else {
      voiceResultText.value = text
      voiceResultVisible.value = true
    }
  } else {
    ElMessage.warning('未识别到语音内容')
  }
}

// 拖拽相关状态
const isDragging = ref(false)
const hasDragged = ref(false)
const position = reactive({ x: null, y: null })
const dragOffset = reactive({ x: 0, y: 0 })

// 计算样式
const assistantStyle = computed(() => {
  if (position.x !== null && position.y !== null) {
    return {
      right: 'auto',
      bottom: 'auto',
      left: `${position.x}px`,
      top: `${position.y}px`
    }
  }
  return {}
})

// 初始化位置（右侧底部）
const initPosition = () => {
  const windowWidth = window.innerWidth
  const windowHeight = window.innerHeight
  position.x = windowWidth - 68
  position.y = windowHeight - 68
}

// 边界检测
const checkBounds = () => {
  if (!assistantRef.value) return
  const rect = assistantRef.value.getBoundingClientRect()
  const windowWidth = window.innerWidth
  const windowHeight = window.innerHeight
  
  // 限制在可视区域内
  if (position.x < 0) position.x = 0
  if (position.y < 0) position.y = 0
  if (position.x + rect.width > windowWidth) position.x = windowWidth - rect.width
  if (position.y + rect.height > windowHeight) position.y = windowHeight - rect.height
}

// 吸附到边缘
const snapToEdge = () => {
  if (!assistantRef.value) return
  const rect = assistantRef.value.getBoundingClientRect()
  const windowWidth = window.innerWidth
  const centerX = position.x + rect.width / 2
  
  // 判断吸附到左边还是右边
  if (centerX < windowWidth / 2) {
    position.x = 16
  } else {
    position.x = windowWidth - rect.width - 16
  }
}

// 开始拖拽
const startDrag = (e) => {
  if (e.button && e.button !== 0) return
  
  isDragging.value = true
  hasDragged.value = false
  
  const clientX = e.touches ? e.touches[0].clientX : e.clientX
  const clientY = e.touches ? e.touches[0].clientY : e.clientY
  
  dragOffset.x = clientX - position.x
  dragOffset.y = clientY - position.y
  
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
  document.addEventListener('touchmove', onDrag, { passive: false })
  document.addEventListener('touchend', stopDrag)
  
  e.preventDefault()
}

// 拖拽中
const onDrag = (e) => {
  if (!isDragging.value) return
  
  const clientX = e.touches ? e.touches[0].clientX : e.clientX
  const clientY = e.touches ? e.touches[0].clientY : e.clientY
  
  const newX = clientX - dragOffset.x
  const newY = clientY - dragOffset.y
  
  // 检测是否移动了（超过 5px 才算拖拽）
  if (Math.abs(newX - position.x) > 5 || Math.abs(newY - position.y) > 5) {
    hasDragged.value = true
  }
  
  position.x = newX
  position.y = newY
  
  if (e.cancelable) e.preventDefault()
}

// 停止拖拽
const stopDrag = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('touchend', stopDrag)
  
  // 边界检测
  checkBounds()
  // 吸附到边缘
  snapToEdge()
  
  // 保存位置到 localStorage
  localStorage.setItem('ai-assistant-position', JSON.stringify({ x: position.x, y: position.y }))
}

// 处理点击（区分点击和拖拽）
const handleClick = () => {
  if (!hasDragged.value) {
    toggleChat()
  }
}

// 加载保存的位置
const loadSavedPosition = () => {
  const saved = localStorage.getItem('ai-assistant-position')
  if (saved) {
    try {
      const { x, y } = JSON.parse(saved)
      position.x = x
      position.y = y
      nextTick(checkBounds)
    } catch {
      initPosition()
    }
  } else {
    initPosition()
  }
}

// 切换聊天窗口
const toggleChat = () => {
  // 收起窗口前触发当前会话记忆沉淀（复用 useChatHistory 同一信号实现）
  if (isChatOpen.value) {
    signalMemoryFlush(activeChatId.value)
  }
  isChatOpen.value = !isChatOpen.value
  if (isChatOpen.value) {
    nextTick(() => {
      scrollToBottom()
    })
  }
}

// 点击外部关闭
const handleClickOutside = (e) => {
  if (!chatWindowRef.value) return
  const target = e.target
  // 点击非三个点按钮区域时关闭下拉菜单
  if (!target.closest('.more-btn-wrapper')) {
    closeDropdown()
  }
  // 聊天窗口内或浮动按钮内 → 不关闭
  if (chatWindowRef.value.contains(target) || target.closest('.ai-float-btn')) {
    return
  }
  // teleported 的下拉面板（el-select / el-date-picker 等）→ 不关闭
  if (target.closest('.el-select-dropdown') || target.closest('.el-popper') || target.closest('.el-picker-panel')) {
    return
  }
  // 输入卡片内的智能体/知识库选择浮层（at-picker / kb-picker，绝对定位向上浮出）→ 不关闭
  if (target.closest('.at-picker') || target.closest('.kb-picker')) {
    return
  }
  // teleported 的溯源/源数据覆盖层（trace-panel / trace-selector / source-panel）→ 不关闭
  if (
    target.closest('.trace-panel') || target.closest('.trace-selector-dialog') ||
    target.closest('.trace-selector-overlay') || target.closest('.source-panel-overlay') ||
    target.closest('.source-panel')
  ) {
    return
  }
  // 关闭窗口前触发当前会话记忆沉淀
  signalMemoryFlush(activeChatId.value)
  isChatOpen.value = false
}

// 历史面板操作后自动收起：选择历史对话 / 面板内新建对话
const selectHistoryItem = (chat) => {
  loadHistoryItem(chat)
  showHistory.value = false
}
const newChatFromHistory = () => {
  handleNewChat()
  showHistory.value = false
}

// 清空全部历史会话（逐个删除后刷新列表）
const clearAllHistory = () => {
  if (historyList.value.length === 0) return
  ElMessageBox.confirm('确定要清空全部历史对话吗？删除后不可恢复。', '清空确认', { type: 'warning' })
    .then(async () => {
      for (const item of historyList.value) {
        try { await deleteChatApi(item.chatId) } catch (e) {}
      }
      await loadHistory()
      ElMessage.success('已清空全部历史对话')
    })
    .catch(() => {})
}

onMounted(() => {
  loadSavedPosition()
  // 无权限时不请求任何接口
  if (hasAiPermission()) {
    loadAllOptions()
    loadHistory()
    loadQuickQuestions()
    loadFaqTree()
  }
  document.addEventListener('mousedown', handleClickOutside)
  window.addEventListener('resize', checkBounds)
  // 语音（配置加载 / 常驻监听 / TTS 订阅）已由 useVoiceChat 的 onMounted 统一处理
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('touchend', stopDrag)
  window.removeEventListener('resize', checkBounds)
  if (kwsWakeTimer) { clearTimeout(kwsWakeTimer); kwsWakeTimer = null }
  // 语音资源（KWS 监听 / TTS 播放 / TTS 订阅）已由 useVoiceChat 的 onBeforeUnmount 统一清理
  // 流式请求中止 / 定时器清理
  dispose()
})
</script>

<style lang="scss" scoped>
// Apple & Xiaomi 柔和设计风格 - 蓝色系
  $primary-gradient: linear-gradient(145deg, #4facfe 0%, #00c6fb 100%);
  $primary-light: #4facfe;
  $primary-dark: #0ea5e9;
$surface-bg: #f5f5f7;
$text-primary: #1d1d1f;
$text-secondary: #86868b;
$text-tertiary: #aeaeb2;
$border-light: rgba(0, 0, 0, 0.04);
$shadow-soft: 0 2px 8px rgba(0, 0, 0, 0.06);
$shadow-medium: 0 4px 20px rgba(0, 0, 0, 0.08);
$shadow-strong: 0 8px 40px rgba(0, 0, 0, 0.12);
$radius-sm: 8px;
$radius-md: 14px;
$radius-lg: 20px;
$radius-xl: 24px;
$radius-full: 9999px;

.ai-assistant {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif;
  touch-action: none;
  user-select: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  &.is-dragging {
    .ai-float-btn {
      cursor: grabbing;
      transform: scale(1.1);
      box-shadow: 0 12px 40px rgba(102, 126, 234, 0.5);
    }
  }
}

// 浮动按钮
.ai-float-btn {
    width: 56px;
    height: 56px;
    border-radius: $radius-full;
    background: $primary-gradient;
    box-shadow: 0 4px 18px rgba(79, 172, 254, 0.5);
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.1);
    position: relative;
    z-index: 10;
    animation: btn-breathe 3s ease-in-out infinite;

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: $radius-full;
    background: $primary-gradient;
    animation: pulse-ring 2.8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    z-index: -1;
  }

  &:hover {
    transform: scale(1.08) translateY(-2px);
    box-shadow: 0 8px 30px rgba(79, 172, 254, 0.55);
    animation: none;
  }

  &:active {
    transform: scale(0.95);
  }

  &.is-active {
    background: linear-gradient(145deg, #8e8e93 0%, #636366 100%);
    box-shadow: 0 4px 14px rgba(142, 142, 147, 0.3);
    animation: none;
    
    &::before {
      background: linear-gradient(145deg, #8e8e93 0%, #636366 100%);
    }
  }

  .ai-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ai-icon {
    width: 40px;
    height: 40px;
    object-fit: contain;
    pointer-events: none;
  }

  .close-icon {
    width: 22px;
    height: 22px;
    color: #fff;
  }
}

@keyframes btn-breathe {
  0%, 100% { box-shadow: 0 4px 18px rgba(79, 172, 254, 0.5); }
  50% { box-shadow: 0 4px 28px rgba(79, 172, 254, 0.75), 0 0 36px rgba(0, 198, 251, 0.25); }
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.35);
    opacity: 0;
  }
  100% {
    transform: scale(1.35);
    opacity: 0;
  }
}

.btn-icon-enter-active,
.btn-icon-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.btn-icon-enter-from,
.btn-icon-leave-to {
  opacity: 0;
  transform: rotate(90deg) scale(0.5);
}

// 聊天窗口 - Apple 风格
  .ai-chat-window {
    position: absolute;
    bottom: 66px;
    right: 0;
    width: 440px;
    height: 660px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(40px) saturate(180%);
    -webkit-backdrop-filter: blur(40px) saturate(180%);
    border-radius: $radius-xl;
    box-shadow: $shadow-strong, 0 0 0 0.5px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

// 标题栏 - 柔和渐变
.chat-header {
  padding: 14px 16px;
  background: $primary-gradient;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;

    .ai-avatar-small {
      width: 34px;
      height: 34px;
      background: rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(10px);
      border-radius: $radius-sm;
      display: flex;
      align-items: center;
      justify-content: center;
      
      svg {
        width: 20px;
        height: 20px;
        color: #fff;
      }
    }

    .header-title {
      color: #fff;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: -0.01em;
    }
  }

  .header-actions {
    display: flex;
    gap: 6px;

    .action-btn {
      width: 30px;
      height: 30px;
      border: none;
      background: rgba(255, 255, 255, 0.18);
      backdrop-filter: blur(10px);
      border-radius: $radius-sm;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.25s ease;
      color: #fff;

      &:hover {
        background: rgba(255, 255, 255, 0.28);
        transform: scale(1.05);
      }

      &:active {
        transform: scale(0.95);
      }

      &.active {
        background: rgba(255, 255, 255, 0.32);
      }

      .el-icon {
        font-size: 15px;
      }
    }
  }
}

@keyframes pulse-status {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.85); }
}

// 配置栏
.config-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #ebeef5;
}

/* 语音设置面板（前端本地可视化设置） */
.ai-settings-panel {
  position: absolute;
  top: 66px;
  right: 10px;
  left: 10px;
  z-index: 40;
  max-width: 380px;
  margin-left: auto;
  max-height: calc(100% - 160px);
  padding: 14px 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(31, 45, 61, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
}

/* 内容区可滚动，控件区不被压缩 */
.settings-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  min-height: 0;
  padding-right: 2px;
}

.settings-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  flex: none;
}

.settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

/* 纵向布局行（如音色下拉，占满整行宽度） */
.settings-row.column {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

.settings-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.settings-label {
  font-size: 13px;
  color: #303133;
}

.settings-desc {
  font-size: 11px;
  color: #909399;
  line-height: 1.5;
  word-break: break-all;
}

.settings-input {
  width: 130px;
  flex: none;
}

.settings-radio {
  flex: none;
}

.settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid #f0f2f5;
  flex: none;
}
.settings-cancel-btn,
.settings-confirm-btn {
  border: none;
  border-radius: 6px;
  padding: 5px 14px;
  font-size: 12px;
  cursor: pointer;
  transition: opacity 0.2s;
}
.settings-cancel-btn {
  background: #f5f5f7;
  color: #606266;
}
.settings-confirm-btn {
  background: $primary-gradient;
  color: #fff;
}
.settings-cancel-btn:hover,
.settings-confirm-btn:hover {
  opacity: 0.85;
}


.config-select {
  flex: 1;
  min-width: 0;

  :deep(.el-select__wrapper) {
    border-radius: 8px;
    box-shadow: none;
    background: #fff;
    border: 1px solid #e4e7ed;
    min-height: 26px;
    padding: 2px 8px;
    font-size: 12px;
  }

  :deep(.el-select__placeholder) {
    color: #a8abb2;
    font-size: 12px;
  }

  :deep(.el-select__selected-item) {
    color: #303133;
    font-size: 12px;
  }

  :deep(.el-select__suffix) {
    color: #909399;
  }
}

.config-select--agent {
  flex: 1.3;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

// 历史对话面板
.history-panel {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(30px);
  z-index: 100;
  display: flex;
  flex-direction: column;

  .history-header {
      padding: 12px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #ebeef5;
      background: #fff;
      flex-shrink: 0;

      .history-header-left {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .history-back-btn {
        width: 28px;
        height: 28px;
        border: none;
        background: #f5f7fa;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #606266;
        transition: all 0.2s ease;

        &:hover {
          background: #ecf5ff;
          color: #409eff;
        }

        .el-icon {
          font-size: 15px;
        }
      }

      .history-title {
        font-size: 15px;
        font-weight: 600;
        color: #303133;
      }

      .history-header-right {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }

  .history-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 4px;
    }
  }

  // 分组样式
  .history-group {
    margin-bottom: 4px;
  }

  .group-header {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    border-radius: 6px;
    transition: background-color 0.2s;
    user-select: none;

    &:hover {
      background-color: #f5f7fa;
    }
  }

  .group-arrow {
    font-size: 12px;
    color: #909399;
    margin-right: 8px;
    transition: transform 0.2s;

    &.is-expanded {
      transform: rotate(90deg);
    }
  }

  .group-label {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
  }

  .group-count {
    font-size: 12px;
    color: #909399;
    margin-left: 4px;
  }

  .group-items {
    padding-left: 8px;
  }

  // 历史项
  .history-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    margin-bottom: 6px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    background: #fff;
    border: 1px solid #e4e7ed;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    &:hover {
      background: #f5f7fa;
      border-color: #c0c4cc;
    }

    &:hover .more-btn {
      opacity: 1;
    }

    &.active {
      background: #ecf5ff;
    }

    .history-item-content {
      flex: 1;
      min-width: 0;

      .history-item-title {
        font-size: 13px;
        color: #303133;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .pin-icon {
        font-size: 12px;
        color: #409eff;
        flex-shrink: 0;
      }

      .history-item-time {
        font-size: 12px;
        color: #909399;
        margin-top: 4px;
      }
    }
  }

  // 三个点更多按钮
  .more-btn-wrapper {
    position: relative;
    flex-shrink: 0;
  }

  .more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s, background-color 0.15s;
    color: #909399;

    &:hover {
      background-color: #e9ecef;
      color: #303133;
    }
  }

  // 下拉菜单
  .dropdown-menu {
    position: absolute;
    right: 0;
    top: 100%;
    z-index: 100;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 4px 0;
    min-width: 120px;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    cursor: pointer;
    font-size: 13px;
    color: #303133;
    transition: background-color 0.15s;

    &:hover {
      background-color: #f5f7fa;
    }

    .el-icon {
      font-size: 14px;
      color: #909399;
    }
  }

  .dropdown-item--danger {
    color: #f56c6c;

    .el-icon {
      color: #f56c6c;
    }

    &:hover {
      background-color: #fef0f0;
    }
  }

  .rename-input-wrapper {
    :deep(.el-input__wrapper) {
      box-shadow: none !important;
      padding: 0;
      background: transparent;
    }

    :deep(.el-input__inner) {
      font-size: 13px;
      font-weight: 600;
      color: #303133;
      padding: 0;
      height: 20px;
      line-height: 20px;
    }
  }

  .history-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: $text-tertiary;
    gap: 12px;

    .history-empty-icon {
      font-size: 48px;
      opacity: 0.3;
    }

    span {
      font-size: 14px;
    }
  }
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter-from,
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

// 消息区域 - 参考 ragChat 风格
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.18);
    }
  }
}

// 欢迎界面 - 精致设计
.welcome-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0 20px;

  .welcome-avatar {
    width: 76px;
    height: 76px;
    background: $primary-gradient;
    border-radius: $radius-lg;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    box-shadow: 0 12px 32px rgba(102, 126, 234, 0.3);
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: $radius-lg;
      background: $primary-gradient;
      opacity: 0.15;
      filter: blur(10px);
    }
    
    svg {
      width: 40px;
      height: 40px;
      color: #fff;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
    }
  }

  .welcome-title {
    font-size: 22px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 6px 0;
    letter-spacing: -0.02em;
  }

  .welcome-desc {
    font-size: 14px;
    color: $text-secondary;
    margin: 0 0 28px 0;
    font-weight: 400;
  }

  .quick-actions {
    width: 100%;

    .quick-actions-title {
      font-size: 13px;
      color: $text-secondary;
      margin-bottom: 12px;
      text-align: left;
      font-weight: 500;
    }

    .quick-actions-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    .quick-action-card {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 14px;
      background: #fff;
      border-radius: $radius-md;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border: 0.5px solid $border-light;

      &:hover {
        background: #fff;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        transform: translateY(-1px);
      }

      &:active {
        transform: scale(0.98);
      }

      .quick-action-icon {
        font-size: 18px;
        flex-shrink: 0;
      }

      .quick-action-text {
        font-size: 12px;
        color: $text-primary;
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-weight: 450;
      }
    }
  }
}

// 消息样式 - 参考 ragChat 风格
.message-item {
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  max-width: 80%;
  animation: message-in 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.user {
    background-color: #ecf5ff;
    align-self: flex-end;
    box-shadow: 0 1px 4px rgba(64, 158, 255, 0.1);
  }

  &.assistant {
    background-color: #fff;
    border: 1px solid #e4e7ed;
    align-self: flex-start;
  }
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.message-time {
  font-size: 12px;
  color: #909399;
}

.message-content {
  font-size: 14px;
  line-height: 1.7;
  word-break: break-word;
  color: #303133;

  // 段落
  :deep(p) {
    margin: 6px 0;
    line-height: 1.6;
  }

  // 标题
  :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
    margin: 10px 0 6px;
    font-weight: 600;
    line-height: 1.4;
    color: #1d1d1f;
  }
  :deep(h1) { font-size: 18px; }
  :deep(h2) { font-size: 16px; }
  :deep(h3) { font-size: 15px; }
  :deep(h4), :deep(h5), :deep(h6) { font-size: 14px; }

  // 列表
  :deep(ul), :deep(ol) {
    padding-left: 22px;
    margin: 6px 0;
  }

  :deep(li) {
    margin: 3px 0;
    line-height: 1.6;
  }

  // 代码块
  :deep(pre) {
    background: #f5f7fa;
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 13px;
    margin: 8px 0;
    border: 1px solid #e4e7ed;
    position: relative;

    code {
      font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
      font-size: 13px;
      background: transparent;
      padding: 0;
      border-radius: 0;
      color: #1d1d1f;
    }

    // 语言标签
    code[data-lang]::before {
      content: attr(data-lang);
      position: absolute;
      top: 4px;
      right: 8px;
      font-size: 11px;
      color: #909399;
      background: #e8eaed;
      padding: 1px 6px;
      border-radius: 4px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 500;
    }

    // 有语言标签时给右侧留出空间
    code[data-lang] {
      display: block;
      padding-right: 60px;
    }
  }

  // 行内代码
  :deep(code) {
    font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    background: rgba(102, 126, 234, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    color: #0ea5e9;
  }

  // 表格
  :deep(table) {
    border-collapse: collapse;
    margin: 8px 0;
    width: 100%;
    font-size: 13px;
  }

  :deep(th), :deep(td) {
    border: 1px solid #dcdfe6;
    padding: 6px 12px;
    text-align: left;
  }

  :deep(th) {
    background: #f5f7fa;
    font-weight: 600;
  }

  // 引用
  :deep(blockquote) {
    border-left: 4px solid #dcdfe6;
    padding-left: 12px;
    color: #606266;
    margin: 8px 0;
    background: #f9fafc;
    border-radius: 0 6px 6px 0;
    padding: 8px 12px;
  }

  // 分割线
  :deep(hr) {
    border: none;
    border-top: 1px solid #e4e7ed;
    margin: 10px 0;
  }

  // 链接
  :deep(a) {
    color: #409eff;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  // 图片
  :deep(img) {
    max-width: 100%;
    border-radius: 6px;
    margin: 6px 0;
  }

  // 删除线
  :deep(del) {
    color: #909399;
  }

  // 加粗/斜体
  :deep(strong) { font-weight: 600; }
  :deep(em) { font-style: italic; }
}

.message-actions {
  display: flex;
  gap: 4px;
  margin-top: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;

  .msg-action-btn {
    width: 26px;
    height: 26px;
    border: none;
    background: #fff;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    &:hover {
      color: #409eff;
      background: rgba(64, 158, 255, 0.08);
    }

    &.is-speaking {
      color: #fff;
      background: linear-gradient(145deg, #ff6b6b 0%, #ee5a24 100%);
      animation: pulse-stop 1.5s ease-in-out infinite;
    }

    .el-icon {
      font-size: 13px;
    }
  }
}

.message-item:hover .message-actions {
  opacity: 1;
}

@keyframes message-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 打字动画 - 参考 ragChat 弹跳效果
// 进度信息显示
.typing-indicator-wrapper {
  padding: 8px 0;
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #606266;
  font-size: 13px;
}

.progress-icon {
  color: #409eff;
  animation: rotating 1s linear infinite;
}

@keyframes rotating {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.progress-node {
  font-weight: 500;
  color: #303133;
}

.progress-detail {
  color: #909399;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 0;

  span {
    width: 8px;
    height: 8px;
    background-color: #909399;
    border-radius: 50%;
    animation: typing-bounce 1.4s infinite ease-in-out;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

@keyframes typing-bounce {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

// 输入区域 - 精致设计
.chat-input-wrapper {
  position: relative;
  padding: 8px 10px 12px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-top: 0.5px solid rgba(0, 0, 0, 0.06);

  /* 悬浮助手面板本身高度有限：澄清卡（父子树 + 搜索）最多占一半，其余内部滚动 */
  :deep(.clarify-card) {
    max-height: 52vh;
    left: 0;
    right: 0;
  }

  .chat-input {
    display: flex;
    gap: 10px;
    align-items: center;

    .message-input {
      flex: 1;

      :deep(.el-input__wrapper) {
        border-radius: $radius-full;
        padding: 6px 14px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        border: 0.5px solid rgba(0, 0, 0, 0.08);
        background: #fff;

        &:hover, &.is-focus {
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.12);
          border-color: rgba(102, 126, 234, 0.3);
        }
      }

      .input-icon {
        color: $text-tertiary;
      }
    }

    .send-btn {
      width: 36px;
      height: 36px;
      flex-shrink: 0;
      border-radius: 50%;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      
      &:not(:disabled) {
        background: $primary-gradient;
        border: none;
      }

      &:hover:not(:disabled) {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.35);
      }

      &:active:not(:disabled) {
        transform: scale(0.95);
      }

      &.stop {
        background: linear-gradient(145deg, #ff6b6b 0%, #ee5a24 100%);
        animation: pulse-stop 1.5s ease-in-out infinite;
      }
    }

    .mic-btn {
      width: 36px;
      height: 36px;
      flex-shrink: 0;
      border-radius: 50%;
      border: 0.5px solid rgba(0, 0, 0, 0.08);
      background: #fff;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.35);
      }

      &.is-recording {
        background: linear-gradient(145deg, #ff6b6b 0%, #ee5a24 100%);
        border: none;
        color: #fff;
        animation: pulse-stop 1.5s ease-in-out infinite;
      }
    }

    .voice-input {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;
    }

    .hold-partial-text {
      flex-basis: 100%;
      text-align: center;
      font-size: 12px;
      color: #606266;
      line-height: 1.4;
      word-break: break-all;
    }

    .hold-to-talk-btn {
      flex: 1;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      border-radius: 18px;
      background: linear-gradient(145deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      cursor: pointer;
      user-select: none;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.2s;

      &:active,
      &.is-recording {
        background: linear-gradient(145deg, #ff6b6b 0%, #ee5a24 100%);
        box-shadow: 0 4px 12px rgba(238, 90, 36, 0.4);
      }

      .hold-icon {
        font-size: 16px;
      }
    }

    .voice-close-btn {
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      color: #8b949e;
    }

  }

  /* 回到最新：悬浮面板空间小，按钮居中压在输入区上方 */
  .btn-scroll-latest {
    position: absolute;
    left: 50%;
    bottom: 108px;
    transform: translateX(-50%);
    z-index: 20;
    padding: 5px 12px;
    font-size: 12px;
    color: #409eff;
    background: #fff;
    border: 1px solid #d6e4ff;
    border-radius: 999px;
    box-shadow: 0 4px 14px rgba(31, 45, 61, 0.14);
    cursor: pointer;
  }

  /* 识别结果确认浮层（微信式）- chat-input-wrapper 直接子级 */
  /* 澄清浮窗样式已内聚到 chat/components/ClarifyCard.vue，此处不再保留同名类，
     以免父组件规则覆盖子组件（同名类在子组件内优先级更低）。 */

  .voice-result-panel {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: calc(100% + 10px);
    z-index: 20;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 14px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 8px 30px rgba(31, 45, 61, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.04);
  }

  .voice-result-label {
    font-size: 12px;
    color: #909399;
  }

  .voice-result-edit {
    width: 100%;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 14px;
    color: #303133;
    line-height: 1.5;
    background: #f8f9fb;
    resize: none;
    outline: none;
    font-family: inherit;
    transition: border-color 0.2s, background 0.2s;
    box-sizing: border-box;
  }

  .voice-result-edit:focus {
    border-color: #667eea;
    background: #fff;
  }

  .voice-result-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
  }

  .voice-result-cancel,
  .voice-result-send {
    border: none;
    border-radius: 16px;
    height: 30px;
    padding: 0 16px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .voice-result-cancel {
    background: #f4f4f5;
    color: #606266;
  }

  .voice-result-cancel:hover {
    background: #e9e9eb;
  }

  .voice-result-send {
    background: linear-gradient(145deg, #667eea 0%, #764ba2 100%);
    color: #fff;
  }

  .voice-result-send:hover {
    transform: scale(1.03);
  }

  .voice-result-enter-active,
  .voice-result-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .voice-result-enter-from,
  .voice-result-leave-to {
    opacity: 0;
    transform: translateY(6px);
  }

  .input-footer {
    margin-top: 10px;
    text-align: center;

    .ai-kws-indicator {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      margin-bottom: 4px;
      font-size: 11px;
      color: $primary-light;

      .ai-kws-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: $primary-light;
        animation: ai-kws-blink 1.2s ease-in-out infinite;
      }
    }

    .footer-text {
      font-size: 11px;
      color: $text-tertiary;
      font-weight: 400;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

// 常驻唤醒监听状态点闪烁
@keyframes ai-kws-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.8); }
}

// 窗口动画 - 弹性效果
.chat-window-enter-active {
  animation: window-in 0.4s cubic-bezier(0.34, 1.4, 0.64, 1);
}

.chat-window-leave-active {
  animation: window-out 0.25s cubic-bezier(0.4, 0, 1, 1);
}

@keyframes window-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes window-out {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
}

@keyframes pulse-stop {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(255, 107, 107, 0); }
}

// 窄窗适配（助手窗 440px，复用聊天页消息/输入组件需收窄内边距）
@media (max-width: 480px) {
  .ai-chat-window {
    :deep(.ragChat-messages) {
      padding: 12px 14px;
    }
    :deep(.chat-input-wrap) {
      margin: 0 4px 4px;
    }
    // ChatInputBar 内置语音设置 chip 隐藏（助手窗自有语音设置面板接管，避免双入口）
    :deep(.voice-setting-wrap) {
      display: none;
    }
    // 智能体 + 知识库上下文栏窄窗适配
    :deep(.agent-context-bar) {
      padding: 6px 8px;
    }
  }
}

// 移动端适配
@media (max-width: 480px) {
  .ai-assistant {
    right: 16px;
    bottom: 16px;
  }

  .ai-chat-window {
    width: calc(100vw - 32px);
    height: calc(100vh - 100px);
    max-height: 600px;
    border-radius: $radius-lg;
  }
}
</style>

<style lang="scss">
// 确认弹窗全局样式（确保在聊天窗口之上显示）
.ai-assistant-confirm {
  z-index: 10002 !important;
}

// 智能体下拉面板（teleported 到 body，需要非 scoped 样式）
.agent-select-dropdown {
  z-index: 10000 !important;
}

// tooltip 遮挡修复
.ai-assistant-tooltip {
  z-index: 10001 !important;
}

// 溯源/源数据覆盖层（chat 组件 Teleport 到 body）→ 需高于助手窗 z-index 9999
.trace-panel {
  z-index: 10010 !important;
}
.trace-selector-overlay {
  z-index: 10020 !important;
}
.source-panel-overlay {
  z-index: 10010 !important;
}
</style>
